import React, { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import _noop from 'lodash-es/noop';
import Spinner from 'sushiweb/Spinner/Spinner';
import red from 'sushiweb/tokens/color/red';
import Breadcrumb from 'sushiweb/Breadcrumb/Breadcrumb';
import {
  BreadCrumbWrapper,
  DashboardWrapper,
  LayoutContainers,
  SpinnerHolder,
} from '../common/styles';
import ErrorPage from '../common/components/ErrorPage';
import { REFRESH_LABEL, programStatusEnum, statusLabels } from '../common/components/constants';
import { StatusContainer } from '../common/components/DataTables/styles';
import { DASHBOARD_ROUTE, HOME_ROUTE, PROGRAMS_ROUTE } from '../../constants/routes';
import ContentCard from '../../components/ContentCard';
import PolicyCard from '../../components/PolicyCard';
import {
  InfoGrid,
  InfoRow,
  InfoLabel,
  InfoValue,
} from '../../components/common/styles';
import ZTrackingClient from '../../jumbo/ZTrackingClient';
import { formatPlanDateTime } from '../../helpers';

const formatAssignedTeams = (groupsData = []) => {
  if (!groupsData?.length) return 'NA';
  return groupsData.map(g => g.group_name).filter(Boolean).join(', ');
};

const Program = ({
  fetchingProgramInfo,
  fetchedProgramInfo,
  programInfo,
  getProgramInfo,
  selectedWorkspace,
}) => {
  const { programId } = useParams();

  const { name = '' } = programInfo;

  useEffect(() => {
    if (programId && selectedWorkspace?.workspace_id) {
      getProgramInfo({
        programId,
        workspaceId: selectedWorkspace.workspace_id,
      });
    }
  }, [programId, selectedWorkspace]);

  useEffect(() => {
    ZTrackingClient.recordImpression({
      ui_source: 'PROGRAM_DETAILS',
      component: 'TABLE_DATA',
    });
  }, [programId]);

  const refreshPage = () => {
    getProgramInfo({
      programId,
      workspaceId: selectedWorkspace.workspace_id,
    });
  };

  if (fetchingProgramInfo) {
    return (
      <DashboardWrapper>
        <SpinnerHolder className="center">
          <Spinner color={red.z_red} size="medium" />
        </SpinnerHolder>
      </DashboardWrapper>
    );
  }

  if (!fetchedProgramInfo) {
    return (
      <DashboardWrapper>
        <ErrorPage
          showAction
          actionLabel={REFRESH_LABEL}
          clickAction={refreshPage}
        />
      </DashboardWrapper>
    );
  }

  return (
    <DashboardWrapper>
      <BreadCrumbWrapper>
        <Breadcrumb>
          <Breadcrumb.Link
            style={{ color: 'var(--coolgreycoolgrey-400, #AFB4C0)' }}
            linkTo={`${DASHBOARD_ROUTE}#${HOME_ROUTE}`}
          >
            Home
          </Breadcrumb.Link>

          <Breadcrumb.Link
            style={{ color: 'var(--coolgreycoolgrey-400, #AFB4C0)' }}
            linkTo={`${DASHBOARD_ROUTE}#${PROGRAMS_ROUTE}`}
          >
            Meal Plans
          </Breadcrumb.Link>

          <Breadcrumb.Link
            style={{ color: 'var(--coolgreycoolgrey-800, #414A5D)' }}
          >
            Meal Plan Details
          </Breadcrumb.Link>
        </Breadcrumb>
      </BreadCrumbWrapper>

      <LayoutContainers>
        <ContentCard>
          <ContentCard.Section>
            <ContentCard.Section>
              <ContentCard.Title>
                {name || 'Meal Plan Details'}

                <StatusContainer
                  style={{ marginLeft: '12px' }}
                  status={programStatusEnum[programInfo?.program_status] ?? 'default'}
                >
                  {statusLabels[programStatusEnum[programInfo?.program_status] ?? 'default']}
                </StatusContainer>
              </ContentCard.Title>

              <InfoGrid>
                <InfoRow>
                  <InfoLabel>Starts on</InfoLabel>
                  <InfoValue>
                    {formatPlanDateTime(programInfo?.start_date, { time: programInfo?.start_time })}
                  </InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>Ends on</InfoLabel>
                  <InfoValue>
                    {formatPlanDateTime(programInfo?.end_date, { neverEnds: programInfo?.never_ends })}
                  </InfoValue>
                </InfoRow>

                <InfoRow>
                  <InfoLabel>Assigned teams</InfoLabel>
                  <InfoValue>{formatAssignedTeams(programInfo?.groups_data)}</InfoValue>
                </InfoRow>
              </InfoGrid>
            </ContentCard.Section>

            <ContentCard.Divider />

            <ContentCard.Section>
              <ContentCard.Subtitle>Food Policy Details</ContentCard.Subtitle>

              {Array.isArray(programInfo?.option_details) && programInfo.option_details.map((policy, index) => (
                <PolicyCard policy={policy} index={index} />
              ))}
            </ContentCard.Section>
          </ContentCard.Section>
        </ContentCard>
      </LayoutContainers>
    </DashboardWrapper>
  );
};

Program.propTypes = {
  fetchingProgramInfo: PropTypes.bool,
  fetchedProgramInfo: PropTypes.bool,
  programInfo: PropTypes.objectOf(PropTypes.any),
  getProgramInfo: PropTypes.func,
  selectedWorkspace: PropTypes.objectOf(PropTypes.any),
};

Program.defaultProps = {
  fetchingProgramInfo: false,
  fetchedProgramInfo: true,
  programInfo: {},
  getProgramInfo: _noop,
  selectedWorkspace: {},
};

export default Program;
